import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mic, Square } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { canRecord, useRecorder } from "@/hooks/useRecorder";

const MAX = 180;
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const ext = (t) => (t.includes("mp4") ? "mp4" : t.includes("ogg") ? "ogg" : "webm");

// Speak into a journal entry; the words are inserted, the audio is never kept
export const JournalMic = ({ onText }) => {
  const [writing, setWriting] = useState(false);
  const rec = useRecorder(async (blob) => {
    setWriting(true);
    try {
      const form = new FormData();
      form.append("audio", blob, `journal.${ext(blob.type)}`);
      const { data } = await api.post("/ai/transcribe", form);
      onText(data.text);
    } catch (e) { toast.error(errMsg(e, "Couldn't write that out. Please try again or type instead.")); }
    finally { setWriting(false); }
  }, MAX);

  const start = async () => {
    if (!canRecord()) return toast.error("Voice isn't supported in this browser. You can still type your entry.");
    try { await rec.start(); }
    catch { toast.error("Microphone access is blocked. Tap the lock icon by the address bar, allow the microphone, then try again."); }
  };

  if (writing) return <span className="inline-flex items-center gap-2 text-sm text-mm-muted" data-testid="journal-mic-writing"><Loader2 size={16} className="animate-spin" />Writing it out…</span>;
  if (rec.state === "recording") return (
    <button type="button" onClick={rec.stop} className="inline-flex items-center gap-2 rounded-full bg-[#D94F7A] text-white px-4 py-2 text-sm font-semibold" data-testid="journal-mic-stop">
      <span className="relative flex h-3 w-3"><span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping motion-reduce:animate-none" /><Square size={12} className="relative fill-current" /></span>
      Stop · <span data-testid="journal-mic-timer">{fmt(rec.seconds)}</span> / {fmt(MAX)}
    </button>
  );
  return (
    <button type="button" onClick={start} className="mm-btn-ghost !px-3 !py-2 text-sm" aria-label="Speak your entry" data-testid="journal-mic-button"><Mic size={16} />Speak</button>
  );
};
